import React, { useState, useEffect } from 'react';
import './Home.css';
import { saveBetaFeedback } from './utils/db';
import { trackUseLastShop } from './utils/analytics';

const RETURN_OPTIONS = [
  { value: 'checking_list',    label: 'Checking a list',        nav: 'basket' },
  { value: 'scanning_receipt', label: 'Scanning a new receipt', nav: 'more'   },
  { value: 'just_exploring',   label: 'Just exploring',         nav: null     },
];

const QUICK_START = [
  { label: 'Milk',      icon: '🥛', aisle: 'fresh_fridge', subcategory: 'milk'  },
  { label: 'Bread',     icon: '🍞', aisle: 'bakery',        subcategory: 'bread' },
  { label: 'Eggs',      icon: '🥚', aisle: 'fresh_fridge', subcategory: 'eggs'  },
  { label: 'All items', icon: null,  aisle: null,            subcategory: null   },
];

const STORE_LABELS = {
  store_tesco:      'Tesco',
  store_sainsburys: "Sainsbury's",
  store_asda:       'Asda',
  store_aldi:       'Aldi',
  store_lidl:       'Lidl',
  store_morrisons:  'Morrisons',
  store_waitrose:   'Waitrose',
};

function getTodayStr() {
  return new Date().toISOString().slice(0, 10);
}

function receiptTimeAgo(date) {
  if (!date) return '';
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function Home({ onNavigate, basketCount = 0, user, latestReceipt, onRecreateList, onAddFromReceipt, onScanReceipt }) {
  const hasBasket      = basketCount > 0;
  const hasReceipt     = !!latestReceipt;
  const isGuest        = !user || user.isAnonymous;
  const firstName      = user?.displayName?.split(' ')[0] || null;
  const initial        = user?.displayName?.[0]?.toUpperCase()
                      || user?.email?.[0]?.toUpperCase()
                      || null;

  // Loading state for async receipt actions
  const [recreating, setRecreating] = useState(false);

  const [showPrompt,      setShowPrompt]      = useState(false);
  const [selectedOption,  setSelectedOption]  = useState(null);
  const [dismissing,      setDismissing]      = useState(false);

  useEffect(() => {
    const today      = getTodayStr();
    const firstVisit = localStorage.getItem('cms_first_visit_date');
    const seen       = localStorage.getItem('cms_seen_return_prompt');

    if (!firstVisit) {
      localStorage.setItem('cms_first_visit_date', today);
      return;
    }
    if (seen) return;
    if (firstVisit !== today) {
      const t = setTimeout(() => setShowPrompt(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAnswer = (opt) => {
    if (selectedOption) return;
    setSelectedOption(opt.value);
    localStorage.setItem('cms_seen_return_prompt', '1');
    saveBetaFeedback('return_visit', { reason: opt.value }).catch(() => {});
    setTimeout(() => {
      setDismissing(true);
      setTimeout(() => {
        setShowPrompt(false);
        if (opt.nav) onNavigate && onNavigate(opt.nav);
      }, 350);
    }, 600);
  };

  // Receipt summary for display
  const receiptStore    = latestReceipt?.detectedStoreId
    ? (STORE_LABELS[latestReceipt.detectedStoreId] || latestReceipt.detectedStoreId.replace('store_', ''))
    : 'Last receipt';
  const receiptItemCount = latestReceipt?.matchedItems?.length || 0;
  const receiptAge      = latestReceipt?.processedAt
    ? receiptTimeAgo(latestReceipt.processedAt)
    : '';
  const receiptSummary  = `${receiptStore} · ${receiptItemCount} items · ${receiptAge}`;

  const handleRecreate = async () => {
    if (recreating) return;
    setRecreating(true);
    trackUseLastShop(receiptItemCount);
    try {
      await onRecreateList?.();
    } finally {
      setRecreating(false);
    }
  };

  const handleAddFrom = async () => {
    if (recreating) return;
    setRecreating(true);
    try {
      await onAddFromReceipt?.();
    } finally {
      setRecreating(false);
    }
  };

  return (
    <div className="home-screen">

      {/* ── User status chip — top right ── */}
      <button
        className={`home-status-chip${isGuest ? ' home-status-chip--guest' : ' home-status-chip--signed-in'}${!isGuest && !firstName ? ' home-status-chip--avatar-only' : ''}`}
        onClick={() => onNavigate && onNavigate('more')}
        aria-label={isGuest ? 'Guest — tap to sign in' : `Signed in as ${user?.displayName || user?.email}`}
      >
        {isGuest ? (
          <>
            <span className="home-status-dot home-status-dot--guest" aria-hidden="true" />
            Guest
          </>
        ) : (
          <>
            <span className="home-status-avatar" aria-hidden="true">{initial}</span>
            {firstName}
          </>
        )}
      </button>

      {/* ── Hero logo ── */}
      <img
        src="/ChopMyShop Logo.svg"
        alt="ChopMyShop"
        className="home-hero-logo"
      />

      {/* ── Centred content ── */}
      <div className="home-center">
        <div className="home-greeting">
          <img
            src="/Penny Fox open eyes.png"
            alt="Penny"
            className="home-penny-inline"
          />
          <div className="home-welcome">
            {/* Greeting text varies by state */}
            {hasBasket ? (
              <span>{`You have ${basketCount} item${basketCount !== 1 ? 's' : ''} in your list`}</span>
            ) : hasReceipt ? (
              <span>Welcome back</span>
            ) : (
              <>
                <span>Start with a few items</span>
                <span className="home-welcome-secondary">Penny compares your list across stores.</span>
              </>
            )}
          </div>
        </div>

        <div className="home-actions">

          {/* ── STATE B: no basket, has valid receipt → Recreate is primary ── */}
          {!hasBasket && hasReceipt ? (
            <>
              <button
                className="home-btn-primary"
                onClick={handleRecreate}
                disabled={recreating}
              >
                {recreating ? 'Loading…' : 'Shop your last list again'}
              </button>
              <p className="home-receipt-summary">{receiptSummary}</p>
              <button
                className="home-btn-text"
                onClick={() => onNavigate && onNavigate('browse')}
              >
                Start a new list
              </button>
            </>
          ) : (
            /* ── STATES A, C, D ── */
            <>
              <button
                className="home-btn-primary"
                onClick={() => onNavigate && onNavigate(hasBasket ? 'basket' : 'browse')}
              >
                {hasBasket ? 'Continue my list' : 'Start a list'}
              </button>

              {/* State C: has basket, no receipt → offer to scan */}
              {hasBasket && !hasReceipt && (
                <button className="home-btn-receipt" onClick={() => onScanReceipt?.()}>
                  <span className="home-btn-receipt-icon">🧾</span> Add items from a receipt <span className="home-btn-receipt-arrow">→</span>
                </button>
              )}

              {/* State D: has basket + receipt → offer to add from last receipt */}
              {hasBasket && hasReceipt && (
                <>
                  <button
                    className="home-btn-receipt"
                    onClick={handleAddFrom}
                    disabled={recreating}
                  >
                    {recreating ? 'Adding…' : <><span className="home-btn-receipt-icon">🧾</span> Add more from your last receipt <span className="home-btn-receipt-arrow">→</span></>}
                  </button>
                  <p className="home-receipt-summary">{receiptSummary}</p>
                </>
              )}
            </>
          )}

        </div>

        {/* Quick-start chips + scan entry — only in State A (no basket, no receipt) */}
        {!hasBasket && !hasReceipt && (
          <>
            <div className="home-quick-start" aria-label="Quick start">
              {QUICK_START.map(chip => (
                <button
                  key={chip.label}
                  className={`home-quick-chip${chip.aisle === null ? ' home-quick-chip--all' : ''}`}
                  onClick={() => onNavigate && onNavigate('browse', chip.aisle, chip.subcategory)}
                >
                  {chip.icon && <span aria-hidden="true">{chip.icon}</span>}
                  {chip.label}
                </button>
              ))}
            </div>
            <div className="home-scan-entry" onClick={() => onScanReceipt?.()}>
              <button className="home-btn-receipt">
                <span className="home-btn-receipt-icon">🧾</span> Add items from your last receipt <span className="home-btn-receipt-arrow">→</span>
              </button>
              <p className="home-scan-entry-sub">Turn your last shop into a list in seconds</p>
            </div>
          </>
        )}
      </div>

      {/* Spacer so fixed card doesn't overlap the primary CTA */}
      {showPrompt && <div className="home-return-spacer" aria-hidden="true" />}

      {/* ── Return visit prompt ── */}
      {showPrompt && (
        <div className={`home-return-prompt${dismissing ? ' home-return-prompt--out' : ''}`}>
          {selectedOption ? (
            <p className="home-return-confirmed">Got it — thanks</p>
          ) : (
            <>
              <p className="home-return-q">What brought you back today?</p>
              <div className="home-return-options">
                {RETURN_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className="home-return-opt"
                    onClick={() => handleAnswer(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
}

export default Home;
